// ============================================================================
// Lightweight CSV Parser — RFC 4180 compliant, zero dependencies
// ============================================================================

/**
 * Parse a CSV string into headers and row objects.
 *
 * Handles:
 * - Comma-separated values
 * - Quoted fields containing commas, newlines, and escaped quotes ("")
 * - \r\n and \n line endings
 * - Empty lines (skipped)
 */
export function parseCsv(
  text: string,
): { headers: string[]; rows: Record<string, string>[] } {
  const lines = parseLines(text);

  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }

  const headers = lines[0].map((h) => h.trim());
  const rows: Record<string, string>[] = [];

  for (let i = 1; i < lines.length; i++) {
    const fields = lines[i];

    // Skip rows that are completely empty
    if (fields.length === 1 && fields[0].trim() === '') continue;

    const row: Record<string, string> = {};
    for (let j = 0; j < headers.length; j++) {
      row[headers[j]] = j < fields.length ? fields[j] : '';
    }
    rows.push(row);
  }

  return { headers, rows };
}

// ============================================================================
// Internal: RFC 4180 line/field parser
// ============================================================================

function parseLines(text: string): string[][] {
  const result: string[][] = [];
  const len = text.length;
  let pos = 0;

  while (pos < len) {
    const { fields, nextPos } = parseRow(text, pos);
    result.push(fields);
    pos = nextPos;
  }

  return result;
}

function parseRow(
  text: string,
  start: number,
): { fields: string[]; nextPos: number } {
  const fields: string[] = [];
  const len = text.length;
  let pos = start;

  while (pos < len) {
    if (text[pos] === '"') {
      // Quoted field
      const { value, nextPos } = parseQuotedField(text, pos);
      fields.push(value);
      pos = nextPos;
    } else {
      // Unquoted field — read until comma or line end
      let end = pos;
      while (end < len && text[end] !== ',' && text[end] !== '\n' && text[end] !== '\r') {
        end++;
      }
      fields.push(text.slice(pos, end));
      pos = end;
    }

    // After a field, expect comma (more fields) or line ending (end of row)
    if (pos < len && text[pos] === ',') {
      pos++; // skip comma, continue to next field

      // If comma is the last thing before EOL or EOF, add empty trailing field
      if (pos >= len || text[pos] === '\n' || text[pos] === '\r') {
        fields.push('');
      }
    } else {
      // End of row — consume line ending
      if (pos < len && text[pos] === '\r') pos++;
      if (pos < len && text[pos] === '\n') pos++;
      break;
    }
  }

  return { fields, nextPos: pos };
}

function parseQuotedField(
  text: string,
  start: number,
): { value: string; nextPos: number } {
  const len = text.length;
  let pos = start + 1; // skip opening quote
  let value = '';

  while (pos < len) {
    if (text[pos] === '"') {
      if (pos + 1 < len && text[pos + 1] === '"') {
        // Escaped quote ("")
        value += '"';
        pos += 2;
      } else {
        // Closing quote
        pos++; // skip closing quote
        break;
      }
    } else {
      value += text[pos];
      pos++;
    }
  }

  return { value, nextPos: pos };
}
